import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { notify } from '../../components/common/AppToast';
import {
  AppFilterBar,
  AppFilterInput,
  AppFilterSelect,
} from '../../components/filters';
import KkpPlanningEmptyState from '../../components/kkp/KkpPlanningEmptyState';
import KkpPlanningPageActions from '../../components/kkp/KkpPlanningPageActions';
import KkpPlanningStickyTableWrap from '../../components/kkp/KkpPlanningStickyTableWrap';
import KkpDistribColorPicker from '../../components/kkp/KkpDistribColorPicker';
import KkpDistribPointModal from '../../components/kkp/KkpDistribPointModal';
import KkpDistribOrdreModal from '../../components/kkp/KkpDistribOrdreModal';
import kkpDistribService from '../../services/kkpDistribService';
import useAppConfirm from '../../hooks/useAppConfirm';
import { AppButton, AppCheckbox, AppFormLabel, AppInput, AppSelect } from '../../components/ui';
import {
  KKP_DISTRIB_PALETTE,
  KKP_DISTRIB_ZONES,
  MAG_PER_PACKET,
  SET_PER_PACKET,
  distribCouleurCible,
  distribCouleurHex,
  formatDistribPhone,
  formatMoisList,
  magPackets,
  setPieces,
} from '../../utils/kkpDistribPalette';
import '../../styles/kkp-planning-page.scss';
import '../../styles/kkp-distrib-page.scss';

const SUPPORT_OPTIONS = [
  { value: 'all', label: 'Tous' },
  { value: 'both', label: 'Mag + Set' },
  { value: 'mag', label: 'Mag' },
  { value: 'set', label: 'Set' },
];

const emptyFilters = {
  zone: [],
  couleur: [],
  search: '',
  presentoir: [],
  support: [],
  ville: [],
};

const toApiFilters = (filters) => {
  const support = filters.support?.[0] || '';
  return {
    zone: filters.zone?.[0] || '',
    couleur: filters.couleur?.[0] || '',
    search: filters.search || '',
    ville: filters.ville?.[0] || '',
    presentoir: filters.presentoir?.[0] === '1' ? true : filters.presentoir?.[0] === '0' ? false : undefined,
    support: support && support !== 'all' ? support : '',
    limit: 1000,
  };
};

const moveItem = (list, fromIndex, toIndex) => {
  const next = [...list];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
};

const KkpDistribPage = () => {
  const { ask, modal } = useAppConfirm();
  const fileInputRef = useRef(null);
  const dragIdRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [villes, setVilles] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [zones, setZones] = useState(KKP_DISTRIB_ZONES);
  const [filters, setFilters] = useState(emptyFilters);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingModal, setSavingModal] = useState(false);
  const [ordres, setOrdres] = useState([]);
  const [selectedOrdreId, setSelectedOrdreId] = useState(null);
  const [ordreModalOpen, setOrdreModalOpen] = useState(false);
  const [savingOrdre, setSavingOrdre] = useState(false);
  const [setPerPacket, setSetPerPacket] = useState(SET_PER_PACKET);
  const [magPerPacket, setMagPerPacket] = useState(MAG_PER_PACKET);
  const [agencePaquets, setAgencePaquets] = useState('0');
  const [agencePaquetsSet, setAgencePaquetsSet] = useState('0');
  const [savingAgenceSet, setSavingAgenceSet] = useState(false);
  const [savingAgence, setSavingAgence] = useState(false);

  const selectedZone = filters.zone?.[0] || '';
  const extraFiltersActive = Boolean(
    filters.search
    || filters.couleur?.[0]
    || filters.presentoir?.[0]
    || (filters.support?.[0] && filters.support[0] !== 'all')
    || filters.ville?.[0]
    || showArchived,
  );
  const canReorder = !showArchived && Boolean(selectedZone) && (Boolean(selectedOrdreId) || !extraFiltersActive);

  const loadOrdres = useCallback(async (zone) => {
    if (!zone) {
      setOrdres([]);
      setSelectedOrdreId(null);
      return;
    }
    try {
      const list = await kkpDistribService.listOrdres(zone);
      setOrdres(list);
      setSelectedOrdreId((prev) => (prev && list.some((o) => o.id === prev) ? prev : null));
    } catch (err) {
      notify.error(err.message);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const apiFilters = toApiFilters(filters);
      const params = { ...apiFilters };
      if (params.presentoir === undefined) delete params.presentoir;
      if (!params.support) delete params.support;
      if (!params.ville) delete params.ville;
      if (showArchived) params.archived = 1;
      const [listRes, totauxRes, agenceRes, meta] = await Promise.all([
        !showArchived && selectedOrdreId
          ? kkpDistribService.getOrdre(selectedOrdreId).then((ordre) => ({
            points: ordre?.points ?? [],
            villes: [],
          }))
          : kkpDistribService.listPoints(params),
        kkpDistribService.getTotaux(showArchived),
        kkpDistribService.getAgenceMag(),
        kkpDistribService.getMeta().catch(() => null),
      ]);
      setPoints(listRes.points);
      if (!selectedOrdreId) setVilles(listRes.villes ?? []);
      setTotaux(totauxRes);
      setAgencePaquets(String(agenceRes?.paquets ?? 0));
      setAgencePaquetsSet(String(agenceRes?.paquets_set ?? 0));
      if (meta?.zones?.length) {
        setZones(meta.zones.map((z) => ({
          key: z.code || z.key,
          label: z.label,
          livreur: z.livreur,
          couleur: z.couleur,
        })));
      }
      if (meta?.set_per_packet) setSetPerPacket(meta.set_per_packet);
      if (meta?.mag_per_packet) setMagPerPacket(meta.mag_per_packet);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedOrdreId, showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadOrdres(selectedZone);
  }, [selectedZone, loadOrdres]);

  const couleurOptions = useMemo(
    () => [
      { value: 'none', label: 'Aucune' },
      ...KKP_DISTRIB_PALETTE.map((c) => ({ value: c.key, label: c.label })),
    ],
    [],
  );

  const patchPoint = async (id, payload) => {
    setSavingId(id);
    try {
      const updated = await kkpDistribService.updatePoint(id, payload);
      setPoints((prev) => prev.map((p) => (p.id === id ? updated : p)));
      const totauxRes = await kkpDistribService.getTotaux(showArchived);
      setTotaux(totauxRes);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSavingId(null);
    }
  };

  const persistOrder = async (nextPoints) => {
    if (!selectedZone) return;
    const numbered = nextPoints.map((p, i) => ({ ...p, ordre_tournee: i + 1 }));
    setPoints(numbered);
    try {
      const saved = selectedOrdreId
        ? await kkpDistribService.reorderOrdre(selectedOrdreId, numbered.map((p) => p.id))
        : await kkpDistribService.reorderPoints(
          selectedZone,
          numbered.map((p) => p.id),
        );
      setPoints(saved);
    } catch (err) {
      notify.error(err.message);
      await load();
    }
  };

  const movePoint = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= points.length) return;
    persistOrder(moveItem(points, index, target));
  };

  const handleDrop = (targetId) => {
    const sourceId = dragIdRef.current;
    dragIdRef.current = null;
    if (!canReorder || !sourceId || sourceId === targetId) return;
    const from = points.findIndex((p) => p.id === sourceId);
    const to = points.findIndex((p) => p.id === targetId);
    if (from < 0 || to < 0) return;
    persistOrder(moveItem(points, from, to));
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const ok = await ask({
      title: 'Importer le catalogue ?',
      message: 'Les points du fichier seront créés ou mis à jour. Ceux absents des listes importées (pour les zones du fichier) seront archivés, pas supprimés. Les autres zones restent intactes.',
      confirmLabel: 'Importer',
      confirmVariant: 'danger',
    });
    if (!ok) {
      return;
    }
    setImporting(true);
    try {
      const result = await kkpDistribService.importExcel(file, { replace: true });
      const parts = [
        `${result.created} créé(s)`,
        `${result.updated} mis à jour`,
      ];
      if (result.restored) parts.push(`${result.restored} restauré(s)`);
      if (result.archived) parts.push(`${result.archived} archivé(s)`);
      const skipped = result.skipped_sheets?.length
        ? ` Onglets ignorés : ${result.skipped_sheets.join(', ')}.`
        : '';
      notify.success(`${result.points} point(s) importé(s) : ${parts.join(', ')}.${skipped}`);
      if (selectedOrdreId) {
        setSelectedOrdreId(null);
      } else {
        await load();
      }
    } catch (err) {
      notify.error(err.message || 'Import impossible');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await kkpDistribService.exportExcel(selectedZone);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async (support) => {
    if (!selectedZone) {
      notify.error('Sélectionnez une zone pour exporter le PDF livreur.');
      return;
    }
    setExporting(true);
    try {
      await kkpDistribService.exportPdf(selectedZone, support, selectedOrdreId);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleSaveModal = async (payload) => {
    setSavingModal(true);
    try {
      if (editing?.id) {
        await kkpDistribService.updatePoint(editing.id, payload);
        notify.success('Point mis à jour');
      } else {
        await kkpDistribService.createPoint(payload);
        notify.success('Point créé');
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSavingModal(false);
    }
  };

  const handleDelete = async (point) => {
    const ok = await ask({
      title: 'Archiver ce point ?',
      message: `Archiver « ${point.nom} » ?`,
      confirmLabel: 'Archiver',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await kkpDistribService.deletePoint(point.id);
      notify.success('Point archivé');
      await load();
    } catch (err) {
      notify.error(err.message);
    }
  };

  const handleCreateOrdre = async (payload) => {
    if (!selectedZone) {
      notify.error('Sélectionnez une zone pour créer un ordre.');
      return;
    }
    setSavingOrdre(true);
    try {
      const created = await kkpDistribService.createOrdre({
        ...payload,
        zone: selectedZone,
      });
      notify.success('Ordre de tournée créé');
      setOrdreModalOpen(false);
      await loadOrdres(selectedZone);
      setSelectedOrdreId(created.id);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSavingOrdre(false);
    }
  };

  const handleDeleteOrdre = async () => {
    const ordre = ordres.find((o) => o.id === selectedOrdreId);
    if (!ordre || !window.confirm(`Supprimer l’ordre « ${ordre.nom} » ?`)) return;
    try {
      await kkpDistribService.deleteOrdre(ordre.id);
      notify.success('Ordre supprimé');
      setSelectedOrdreId(null);
      await loadOrdres(selectedZone);
    } catch (err) {
      notify.error(err.message);
    }
  };

  const selectZone = (zone) => {
    setSelectedOrdreId(null);
    setFilters((f) => ({ ...f, zone: f.zone?.[0] === zone ? [] : [zone], ville: [] }));
  };

  const global = totaux?.global;
  const parZone = totaux?.par_zone ?? [];
  const selectedZoneMeta = zones.find((z) => z.key === selectedZone);
  const supportLabel = SUPPORT_OPTIONS.find((o) => o.value === (filters.support?.[0] || 'all'))?.label || 'Tous';

  const saveAgencePaquets = async () => {
    const paquets = Math.max(0, parseInt(agencePaquets, 10) || 0);
    setAgencePaquets(String(paquets));
    setSavingAgence(true);
    try {
      const saved = await kkpDistribService.saveAgenceMag({ paquets });
      setAgencePaquets(String(saved?.paquets ?? paquets));
      setAgencePaquetsSet(String(saved?.paquets_set ?? agencePaquetsSet));
      const totauxRes = await kkpDistribService.getTotaux(showArchived);
      setTotaux(totauxRes);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSavingAgence(false);
    }
  };

  const saveAgencePaquetsSet = async () => {
    const paquetsSet = Math.max(0, parseInt(agencePaquetsSet, 10) || 0);
    setAgencePaquetsSet(String(paquetsSet));
    setSavingAgenceSet(true);
    try {
      const saved = await kkpDistribService.saveAgenceMag({ paquets_set: paquetsSet });
      setAgencePaquetsSet(String(saved?.paquets_set ?? paquetsSet));
      setAgencePaquets(String(saved?.paquets ?? agencePaquets));
      const totauxRes = await kkpDistribService.getTotaux(showArchived);
      setTotaux(totauxRes);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSavingAgenceSet(false);
    }
  };

  const includeAgenceInFilter = !selectedZone && !extraFiltersActive && !selectedOrdreId;
  const agenceMagazines = Math.max(0, parseInt(agencePaquets, 10) || 0) * magPerPacket;
  const agenceSets = Math.max(0, parseInt(agencePaquetsSet, 10) || 0);

  const filteredStats = useMemo(() => {
    const qteMag = points.reduce((sum, p) => sum + (Number(p.qte_mag) || 0), 0)
      + (includeAgenceInFilter ? agenceMagazines : 0);
    const qteSet = points.reduce((sum, p) => sum + (Number(p.qte_set) || 0), 0)
      + (includeAgenceInFilter ? agenceSets : 0);
    return {
      points: points.length,
      qteMag,
      paquetsMag: magPackets(qteMag, magPerPacket),
      qteSet,
      sets: setPieces(qteSet, setPerPacket),
    };
  }, [points, magPerPacket, setPerPacket, includeAgenceInFilter, agenceMagazines, agenceSets]);

  const selectedOrdre = ordres.find((o) => o.id === selectedOrdreId);
  const filterScope = [
    selectedZoneMeta ? `${selectedZoneMeta.label}${selectedZoneMeta.livreur ? ` · ${selectedZoneMeta.livreur}` : ''}` : 'Toutes zones',
    selectedOrdre ? selectedOrdre.nom : null,
    filters.ville?.[0] || null,
    filters.support?.[0] && filters.support[0] !== 'all' ? supportLabel : null,
    filters.couleur?.[0]
      ? (filters.couleur[0] === 'none'
        ? 'Sans couleur'
        : (KKP_DISTRIB_PALETTE.find((c) => c.key === filters.couleur[0])?.label || filters.couleur[0]))
      : null,
  ].filter(Boolean).join(' · ');

  return (
    <MasterLayout>
      <Breadcrumb
        title="Points de dépôt"
        subtitle="Distribution MAG / SET · catalogue, ordre de tournée et export livreur"
        actions={(
          <KkpPlanningPageActions
            actions={[
              {
                key: 'create',
                label: 'Nouveau point',
                icon: 'mdi:plus',
                variant: 'primary',
                onClick: () => { setEditing(null); setModalOpen(true); },
              },
            ]}
            menuItems={[
              {
                key: 'import',
                label: importing ? 'Import…' : 'Importer Excel',
                icon: 'mdi:upload',
                onClick: handleImportClick,
                disabled: importing,
              },
              {
                key: 'export',
                label: exporting ? 'Export…' : 'Exporter Excel',
                icon: 'mdi:download',
                onClick: handleExport,
                disabled: exporting,
              },
              {
                key: 'pdf-set',
                label: 'PDF tournée SET',
                icon: 'mdi:file-pdf-box',
                onClick: () => handleExportPdf('set'),
                disabled: exporting || !selectedZone,
              },
              {
                key: 'pdf-mag',
                label: 'PDF tournée MAG',
                icon: 'mdi:file-pdf-box',
                onClick: () => handleExportPdf('mag'),
                disabled: exporting || !selectedZone,
              },
              {
                key: 'pdf-both',
                label: 'PDF MAG + SET',
                icon: 'mdi:file-pdf-box',
                onClick: () => handleExportPdf('both'),
                disabled: exporting || !selectedZone,
              },
            ]}
          />
        )}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xlsm,.xls,.csv"
        className="d-none"
        onChange={handleImportFile}
      />

      <div className="row g-3 mb-3">
        <div className="col-6 col-lg">
          <div className="card kkp-planning-kpi-card">
            <div className="card-body">
              <span className="kkp-planning-kpi-card__label">Points</span>
              <strong>{global?.points ?? '—'}</strong>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg">
          <div className="card kkp-planning-kpi-card">
            <div className="card-body">
              <span className="kkp-planning-kpi-card__label">Magazines</span>
              <strong>{global?.qte_mag?.toLocaleString('fr-FR') ?? '—'}</strong>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg">
          <div className="card kkp-planning-kpi-card">
            <div className="card-body">
              <span className="kkp-planning-kpi-card__label">Paquets MAG (÷{magPerPacket})</span>
              <strong>{global?.paquets_mag ?? '—'}</strong>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg">
          <div className="card kkp-planning-kpi-card h-100">
            <div className="card-body">
              <span className="kkp-planning-kpi-card__label">Paquets agence</span>
              <div className="d-flex align-items-center gap-2 mt-1">
                <AppInput
                  id="agence-paquets"
                  size="sm"
                  type="number"
                  min="0"
                  aria-label="Paquets de magazines agence"
                  value={agencePaquets}
                  onChange={(e) => setAgencePaquets(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveAgencePaquets();
                  }}
                />
                <AppButton
                  size="sm"
                  variant="primary"
                  icon="mdi:content-save"
                  title="Enregistrer"
                  aria-label="Enregistrer les paquets agence"
                  loading={savingAgence}
                  onClick={saveAgencePaquets}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg">
          <div className="card kkp-planning-kpi-card">
            <div className="card-body">
              <span className="kkp-planning-kpi-card__label">Paquets SET</span>
              <strong>{global?.qte_set ?? '—'}</strong>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg">
          <div className="card kkp-planning-kpi-card h-100">
            <div className="card-body">
              <span className="kkp-planning-kpi-card__label">Paquets agence SET</span>
              <div className="d-flex align-items-center gap-2 mt-1">
                <AppInput
                  id="agence-paquets-set"
                  size="sm"
                  type="number"
                  min="0"
                  aria-label="Paquets de sets agence"
                  value={agencePaquetsSet}
                  onChange={(e) => setAgencePaquetsSet(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveAgencePaquetsSet();
                  }}
                />
                <AppButton
                  size="sm"
                  variant="primary"
                  icon="mdi:content-save"
                  title="Enregistrer"
                  aria-label="Enregistrer les paquets agence SET"
                  loading={savingAgenceSet}
                  onClick={saveAgencePaquetsSet}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-lg">
          <div className="card kkp-planning-kpi-card">
            <div className="card-body">
              <span className="kkp-planning-kpi-card__label">Sets (×{setPerPacket})</span>
              <strong>{global?.sets?.toLocaleString('fr-FR') ?? '—'}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {parZone.map((z) => {
          const active = selectedZone === z.zone;
          return (
            <div className="col-12 col-md-4 col-xl-3" key={z.zone}>
              <button
                type="button"
                className={`card kkp-planning-kpi-card kkp-distrib-kpi w-100 text-start ${active ? 'is-active' : ''}`}
                onClick={() => selectZone(z.zone)}
              >
                <div className="card-body">
                  <span className="kkp-planning-kpi-card__label">
                    <span className="kkp-distrib-zone-dot" style={{ background: z.couleur || '#64748b' }} />
                    {z.label} · {z.livreur || '—'}
                  </span>
                  <strong>{z.points} pts</strong>
                  <div className="text-secondary-light text-sm mt-1">
                    {z.qte_mag.toLocaleString('fr-FR')} mag · {z.qte_set} paq. SET · {(z.sets ?? setPieces(z.qte_set, setPerPacket)).toLocaleString('fr-FR')} sets
                  </div>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {!selectedZone ? (
        <p className="text-secondary-light text-sm mb-3">
          Filtrez une zone pour réordonner la tournée, créer un ordre spécifique et exporter le PDF livreur.
        </p>
      ) : extraFiltersActive && !selectedOrdreId ? (
        <p className="text-secondary-light text-sm mb-3">
          Réinitialisez les autres filtres pour réordonner le catalogue, ou sélectionnez un ordre spécifique.
        </p>
      ) : (
        <p className="text-secondary-light text-sm mb-3">
          Glissez les lignes ou utilisez les flèches pour changer l’ordre
          {selectedOrdreId ? ' de cet ordre spécifique' : ' catalogue de la zone'}.
          Le PDF n’inclut pas les points à quantité 0.
        </p>
      )}

      {selectedZone ? (
        <div className="kkp-distrib-ordre-bar mb-3">
          <AppFormLabel htmlFor="distrib-ordre" className="form-label mb-0">Ordre de tournée</AppFormLabel>
          <AppSelect
            id="distrib-ordre"
            size="sm"
            className="w-auto"
            value={selectedOrdreId || ''}
            onChange={(e) => setSelectedOrdreId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Catalogue</option>
            {ordres.map((ordre) => (
              <option key={ordre.id} value={ordre.id}>
                {ordre.nom}
                {ordre.mois?.length ? ` · ${formatMoisList(ordre.mois)}` : ''}
              </option>
            ))}
          </AppSelect>
          <AppButton
            size="sm"
            variant="outline-primary"
            icon="mdi:plus"
            onClick={() => setOrdreModalOpen(true)}
          >
            Nouvel ordre
          </AppButton>
          {selectedOrdreId ? (
            <AppButton
              size="sm"
              variant="outline-danger"
              onClick={handleDeleteOrdre}
            >
              Supprimer l’ordre
            </AppButton>
          ) : null}
        </div>
      ) : null}

      <AppFilterBar onReset={() => { setSelectedOrdreId(null); setFilters(emptyFilters); setShowArchived(false); }}>
        <AppFilterInput
          id="distrib-search"
          label="Recherche"
          placeholder="Nom, ville, commentaire…"
          value={filters.search}
          onChange={(value) => setFilters((f) => ({ ...f, search: value }))}
        />
        <AppFilterSelect
          id="distrib-zone"
          label="Zone"
          col="col-6 col-md-2"
          multiple={false}
          value={filters.zone}
          onChange={(next) => {
            setSelectedOrdreId(null);
            setFilters((f) => ({ ...f, zone: next, ville: [] }));
          }}
          options={zones.map((z) => ({ value: z.key, label: z.label }))}
        />
        <AppFilterSelect
          id="distrib-ville"
          label="Ville"
          col="col-6 col-md-2"
          multiple={false}
          value={filters.ville}
          onChange={(next) => setFilters((f) => ({ ...f, ville: next }))}
          options={villes.map((ville) => ({ value: ville, label: ville }))}
        />
        <AppFilterSelect
          id="distrib-couleur"
          label="Couleur"
          col="col-6 col-md-2"
          multiple={false}
          value={filters.couleur}
          onChange={(next) => setFilters((f) => ({ ...f, couleur: next }))}
          options={couleurOptions}
        />
        <AppFilterSelect
          id="distrib-presentoir"
          label="Présentoir"
          col="col-6 col-md-2"
          multiple={false}
          value={filters.presentoir}
          onChange={(next) => setFilters((f) => ({ ...f, presentoir: next }))}
          options={[
            { value: '1', label: 'Oui' },
            { value: '0', label: 'Non' },
          ]}
        />
        <AppFilterSelect
          id="distrib-support"
          label="Support"
          col="col-6 col-md-2"
          multiple={false}
          searchable={false}
          placeholder="Tous"
          value={filters.support}
          onChange={(next) => setFilters((f) => ({ ...f, support: next }))}
          options={SUPPORT_OPTIONS}
        />
        <div className="col-6 col-md-2 d-flex align-items-end pb-1">
          <AppCheckbox
            id="distrib-archived"
            label="Archivés"
            checked={showArchived}
            onChange={(e) => {
              setSelectedOrdreId(null);
              setShowArchived(e.target.checked);
            }}
          />
        </div>
      </AppFilterBar>

      <div className="kkp-distrib-filter-stats mb-3">
        <span className="kkp-distrib-filter-stats__scope">{filterScope}</span>
        <div className="kkp-distrib-filter-stats__metrics">
          <div>
            <span>Points</span>
            <strong>{loading ? '—' : filteredStats.points}</strong>
          </div>
          <div>
            <span>Magazines</span>
            <strong>{loading ? '—' : filteredStats.qteMag.toLocaleString('fr-FR')}</strong>
          </div>
          <div>
            <span>Paquets MAG</span>
            <strong>{loading ? '—' : filteredStats.paquetsMag}</strong>
          </div>
          <div>
            <span>Sets</span>
            <strong>{loading ? '—' : filteredStats.sets.toLocaleString('fr-FR')}</strong>
          </div>
          <div>
            <span>Paquets SET</span>
            <strong>{loading ? '—' : filteredStats.qteSet.toLocaleString('fr-FR')}</strong>
          </div>
        </div>
      </div>

      {loading ? (
        <KkpPlanningEmptyState loading />
      ) : points.length === 0 ? (
        <KkpPlanningEmptyState
          message={showArchived
            ? 'Aucun point archivé ne correspond aux filtres.'
            : selectedZone || extraFiltersActive
            ? 'Aucun point ne correspond aux filtres.'
            : 'Aucun point de dépôt. Importez le classeur KKP Distrib pour démarrer.'}
          actions={selectedZone || extraFiltersActive ? [] : [
            { key: 'import', label: 'Importer Excel', icon: 'mdi:upload', variant: 'primary', onClick: handleImportClick },
          ]}
        />
      ) : (
        <KkpPlanningStickyTableWrap>
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: '4.5rem' }} />
                <th style={{ width: '9rem' }}>
                  Couleur
                  <div className="text-secondary-light fw-normal" style={{ fontSize: '0.65rem', textTransform: 'none', letterSpacing: 0 }}>
                    Bleu/vert = nom · Jaune/rouge = commentaire
                  </div>
                </th>
                <th>Ordre</th>
                <th>Point</th>
                <th>Ville</th>
                <th>MAG</th>
                <th>SET</th>
                <th>Retour MAG</th>
                <th>Retour SET</th>
                <th>Présentoir</th>
                <th>Commentaire</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {points.map((point, index) => {
                const hex = point.couleur_hex || distribCouleurHex(point.couleur);
                const cible = point.couleur_cible || distribCouleurCible(point.couleur);
                return (
                  <tr
                    key={point.id}
                    className={`kkp-distrib-row ${canReorder ? 'is-sortable' : ''}`}
                    draggable={canReorder}
                    onDragStart={() => { dragIdRef.current = point.id; }}
                    onDragOver={(e) => { if (canReorder) e.preventDefault(); }}
                    onDrop={() => handleDrop(point.id)}
                  >
                    <td className="text-nowrap">
                      <AppButton
                        size="sm"
                        variant="link"
                        className="text-secondary p-0 kkp-distrib-drag"
                        title={canReorder ? 'Glisser pour réordonner' : 'Filtrez une zone pour réordonner'}
                        disabled={!canReorder}
                        icon="mdi:drag"
                        iconWidth={18}
                      />
                      <AppButton
                        size="sm"
                        variant="link"
                        className="p-0 ms-1"
                        title="Monter"
                        disabled={!canReorder || index === 0}
                        onClick={() => movePoint(index, -1)}
                        icon="mdi:chevron-up"
                        iconWidth={18}
                      />
                      <AppButton
                        size="sm"
                        variant="link"
                        className="p-0"
                        title="Descendre"
                        disabled={!canReorder || index === points.length - 1}
                        onClick={() => movePoint(index, 1)}
                        icon="mdi:chevron-down"
                        iconWidth={18}
                      />
                    </td>
                    <td>
                      <KkpDistribColorPicker
                        value={point.couleur}
                        disabled={savingId === point.id}
                        onChange={(couleur) => patchPoint(point.id, { couleur })}
                      />
                    </td>
                    <td>{point.ordre_tournee}</td>
                    <td>
                      <div
                        className={`fw-semibold ${cible === 'nom' && hex ? 'kkp-distrib-hl' : ''}`}
                        style={cible === 'nom' && hex ? { backgroundColor: hex } : undefined}
                      >
                        {point.nom}
                      </div>
                      <div className="text-secondary-light text-sm">
                        {[point.adresse, formatDistribPhone(point.telephone)].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td>
                      <div>{point.ville || '—'}</div>
                      <div className="text-secondary-light text-sm">{point.code_postal}</div>
                    </td>
                    <td>
                      <AppInput
                        type="number"
                        min="0"
                        size="sm"
                        className="kkp-distrib-qty-input"
                        defaultValue={point.qte_mag}
                        key={`mag-${point.id}-${point.qte_mag}`}
                        disabled={savingId === point.id}
                        onBlur={(e) => {
                          const next = Number(e.target.value) || 0;
                          if (next !== point.qte_mag) patchPoint(point.id, { qte_mag: next });
                        }}
                      />
                    </td>
                    <td>
                      <AppInput
                        type="number"
                        min="0"
                        size="sm"
                        className="kkp-distrib-qty-input"
                        defaultValue={point.qte_set}
                        key={`set-${point.id}-${point.qte_set}`}
                        disabled={savingId === point.id}
                        onBlur={(e) => {
                          const next = Number(e.target.value) || 0;
                          if (next !== point.qte_set) patchPoint(point.id, { qte_set: next });
                        }}
                      />
                      <div className="text-secondary-light text-sm mt-1">
                        {setPieces(point.qte_set, setPerPacket).toLocaleString('fr-FR')} sets
                      </div>
                    </td>
                    <td>
                      <AppInput
                        type="number"
                        min="0"
                        size="sm"
                        className="kkp-distrib-qty-input"
                        defaultValue={point.retour_mag ?? 0}
                        key={`rmag-${point.id}-${point.retour_mag}`}
                        disabled={savingId === point.id}
                        onBlur={(e) => {
                          const next = Number(e.target.value) || 0;
                          if (next !== (point.retour_mag ?? 0)) patchPoint(point.id, { retour_mag: next });
                        }}
                      />
                    </td>
                    <td>
                      <AppInput
                        type="number"
                        min="0"
                        size="sm"
                        className="kkp-distrib-qty-input"
                        defaultValue={point.retour_set ?? 0}
                        key={`rset-${point.id}-${point.retour_set}`}
                        disabled={savingId === point.id}
                        onBlur={(e) => {
                          const next = Number(e.target.value) || 0;
                          if (next !== (point.retour_set ?? 0)) patchPoint(point.id, { retour_set: next });
                        }}
                      />
                    </td>
                    <td>
                      <AppCheckbox
                        checked={Boolean(point.presentoir)}
                        disabled={savingId === point.id}
                        title={point.presentoir ? 'Présentoir présent' : 'Pas de présentoir'}
                        aria-label={`Présentoir ${point.nom}`}
                        onChange={(e) => patchPoint(point.id, { presentoir: e.target.checked })}
                      />
                    </td>
                    <td>
                      <div
                        className={`kkp-distrib-comment ${cible === 'commentaire' && hex ? 'kkp-distrib-hl' : ''}`}
                        style={cible === 'commentaire' && hex ? { backgroundColor: hex } : undefined}
                      >
                        {point.commentaire || '—'}
                      </div>
                    </td>
                    <td className="text-nowrap">
                      <AppButton
                        size="sm"
                        variant="outline-secondary"
                        className="me-1"
                        title="Modifier"
                        icon="mdi:pencil-outline"
                        onClick={() => { setEditing(point); setModalOpen(true); }}
                      />
                      <AppButton
                        size="sm"
                        variant="outline-danger"
                        title="Archiver"
                        icon="mdi:archive-outline"
                        onClick={() => handleDelete(point)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </KkpPlanningStickyTableWrap>
      )}

      <KkpDistribPointModal
        show={modalOpen}
        point={editing}
        zones={zones}
        saving={savingModal}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSubmit={handleSaveModal}
      />
      <KkpDistribOrdreModal
        show={ordreModalOpen}
        zoneLabel={selectedZoneMeta?.label}
        saving={savingOrdre}
        onClose={() => setOrdreModalOpen(false)}
        onSubmit={handleCreateOrdre}
      />
      {modal}
    </MasterLayout>
  );
};

export default KkpDistribPage;
